import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { RicsSurveyForm } from '../components/survey'
import useSurveyStore from '../stores/surveyStore'
import useMobileLayout from '../hooks/useMobileLayout'

export default function EditSurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useMobileLayout()
  const { currentSurvey, loadSurvey, loading, error } = useSurveyStore()

  // Use local state to store initial survey data - this prevents re-renders when store updates from auto-save
  const [initialSurveyData, setInitialSurveyData] = useState(null)
  const [loadedSurveyId, setLoadedSurveyId] = useState(null)

  // Check if user explicitly wants desktop view (for tablets)
  const forceDesktop = searchParams.get('view') === 'desktop'

  useEffect(() => {
    // Only load survey if it's not already the current one
    // This prevents unnecessary API calls when navigating from create
    if (!id) return

    // Handle both numeric IDs (local surveys) and UUID strings (remote surveys)
    // Check if it's a UUID format (contains hyphens) - if so, keep as string
    // Otherwise try to parse as number
    const isUUID = id.includes('-')
    const surveyId = isUUID ? id : parseInt(id, 10)
    const targetId = surveyId.toString()

    // Check if we need to load a different survey
    if (loadedSurveyId !== targetId) {
      // Reset state when loading a different survey
      setInitialSurveyData(null)
      setLoadedSurveyId(targetId)
      loadSurvey(surveyId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, loadedSurveyId])

  // Set initial data once when survey is loaded
  useEffect(() => {
    if (currentSurvey && !initialSurveyData && loadedSurveyId) {
      const currentId = currentSurvey.id?.toString()
      if (currentId === loadedSurveyId) {
        setInitialSurveyData(currentSurvey)
      }
    }
  }, [currentSurvey, initialSurveyData, loadedSurveyId])

  // Redirect mobile users to the photo capture page which has mobile layout
  // BUT allow tablets to override with ?view=desktop parameter
  useEffect(() => {
    console.log('[EditSurveyPage] Mobile redirect check:', { isMobile, id, windowWidth: window.innerWidth, forceDesktop })
    if (isMobile && id && !forceDesktop) {
      console.log('[EditSurveyPage] Redirecting to mobile photos page')
      navigate(`/survey/${id}/photos`, { replace: true })
    }
  }, [isMobile, id, forceDesktop, navigate])

  // Show error message if survey couldn't be loaded
  if (error) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-900 mb-2">Survey Not Found</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <p className="text-sm text-red-600 mb-4">
            This may be because the survey was deleted or synced to the cloud with a new ID.
            Please check the survey list for the current survey.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  if (loading || !initialSurveyData) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-12 bg-slate-200 rounded"></div>
            <div className="h-12 bg-slate-200 rounded"></div>
            <div className="h-12 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <RicsSurveyForm
        key={loadedSurveyId}
        initialData={initialSurveyData}
        onSubmit={() => navigate(`/survey/${id}`)}
      />
    </div>
  )
}
